/**
 * The mini game's panel (Doom 10-17, approved side panel "Penelope · mini game V1"): the liquidity-dropout mini game's
 * own row — its switch, its live state and reason, and its settings in four sections — in the approved dark navy
 * layout. One component, used in the Penelope ticket (the market sidebar) and on Game Detail. Settings save as a field
 * is left, through the row's ordinary PATCH; a field that does not parse or is out of range says so and saves nothing,
 * so a bad draft never touches a working attempt. Running attempts keep their prices and deadlines (the relay reads
 * the new values for new attempts only). The read-only text says what V1 does, not what the packet asked for.
 * Josh 10-29 review (b9bde14ca's open item): the mode (pair / value) is shown and switched here; in value mode the value
 * settings replace the pair-only ones (the pair spread, the bid offset, orphan completions).
 */
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/infra/utils';
import type { PenelopeConfig, PenelopeHarvesterControl } from '@/hooks/usePenelope';
import type { PenelopeBound } from '@/hooks/usePenelopeProfile';
import { penelopeStatusSentence } from '@/views/LiveGame/penelopeStatus';
import type { GameRow } from '@/views/ValueDashboard/types';

/** The relay's mini-game start (config.ts MINIGAME_START plus the mg* defaults), shown before a row exists. */
export const MINI_START: Partial<PenelopeConfig> = {
  clipContracts: 25, pairLockCents: 1, maxNetContracts: 500, leanMaxNetContracts: 500,
  mgOffsetTicks: 0, mgLifetimeMs: 3_000, mgStartIntervalMs: 1_000, mgDropPct: 0.4, mgRecoverPct: 0.6, mgConfirmMs: 1_000, mgWindowMs: 10_000,
  mgTriggerMethod: 'percent', mgAbsThreshold: 5_000,
  // Doom 10-29 value mode (relay DEFAULT_PENELOPE_CONFIG): pair stays the default.
  mgMode: 'pair', mgValueClipContracts: 10, mgValueDropoutCapContracts: 30, mgValuePullOnRecover: true, mgValuePullOnEdge: true, mgValueEdgeFloorCents: 0, mgValueExitOnValue: false,
  // Penelope's game kill applies to the mini game's row too (relay DEFAULT_PENELOPE_CONFIG.gameKillUsd); until
  // 2026-10-21 the panel neither showed nor edited it, and a tripped row read as "not started".
  gameKillUsd: 50,
};

type Key = 'clipContracts' | 'pairLockCents' | 'mgOffsetTicks' | 'mgLifetimeMs' | 'mgStartIntervalMs' | 'maxNetContracts' | 'gameKillUsd' | 'mgDropPct' | 'mgRecoverPct' | 'mgConfirmMs' | 'mgAbsThreshold'
  | 'mgValueClipContracts' | 'mgValueDropoutCapContracts' | 'mgValueEdgeFloorCents';
type Toggle = 'mgValuePullOnRecover' | 'mgValuePullOnEdge' | 'mgValueExitOnValue';
interface Field {
  key: Key;
  label: string;
  help: string;
  unit: string;
  /** Stored value → the number shown, and back. */
  show: (v: number) => number;
  store: (v: number) => number;
  min: number;
  max: number;
  step: number;
  /** Whole numbers only (in the shown unit). */
  whole?: boolean;
}
const same = (v: number) => v;
const sec = { show: (ms: number) => ms / 1000, store: (s: number) => Math.round(s * 1000) };
const pct = { show: (f: number) => Math.round(f * 100), store: (p: number) => p / 100 };

const PAIR: Field[] = [
  // Size maxima are the box's (the relay's bounds, passed in); without them the relay is the only check.
  // R9: the same names every Penelope screen uses (Contract Size, Max Long), the panel's own words beside them.
  { key: 'clipContracts', label: 'Contract Size (per side)', help: 'Equal size on the two teams.', unit: 'ctrs', show: same, store: same, min: 1, max: Infinity, step: 1, whole: true },
  { key: 'pairLockCents', label: 'Min pair spread', help: 'Gross spread if both sides fill: the two prices sum to at most 100 less this.', unit: '¢', show: same, store: same, min: 0, max: 20, step: 1, whole: true },
  { key: 'mgOffsetTicks', label: 'Bid offset', help: '0 joins each best bid (other people’s). A tick is 1¢ on these markets.', unit: 'ticks', show: same, store: same, min: 0, max: 10, step: 1, whole: true },
  { key: 'mgLifetimeMs', label: 'Attempt lifetime', help: 'From when its two orders went in; she cancels what is left then.', unit: 's', ...sec, min: 0.25, max: 600, step: 0.25 },
  { key: 'mgStartIntervalMs', label: 'Start interval', help: 'Minimum time between new attempts.', unit: 's', ...sec, min: 0, max: 600, step: 0.25 },
];
/** Doom 10-29 value mode: its numbers (the lifetime and the interval are shared with pair mode), then its three toggles. */
const VALUE: Field[] = [
  { key: 'mgValueClipContracts', label: 'Value order size', help: 'Each value order, on the team in its own dropout that shows edge to add against the live Pinnacle line.', unit: 'ctrs', show: same, store: same, min: 1, max: Infinity, step: 1, whole: true },
  { key: 'mgValueDropoutCapContracts', label: 'Cap per dropout', help: 'The most value orders may buy on one team in one dropout; it resets when that team recovers.', unit: 'ctrs', show: same, store: same, min: 1, max: Infinity, step: 5, whole: true },
  { key: 'mgValueEdgeFloorCents', label: 'Edge floor', help: 'With pull on edge loss on, a resting value order comes off when its edge to the live line falls under this.', unit: '¢', show: same, store: same, min: -5, max: 10, step: 0.5 },
  { key: 'mgLifetimeMs', label: 'Order lifetime', help: 'From when it went in; she cancels what is left then.', unit: 's', ...sec, min: 0.25, max: 600, step: 0.25 },
  { key: 'mgStartIntervalMs', label: 'Start interval', help: 'Minimum time between two value orders on the same team.', unit: 's', ...sec, min: 0, max: 600, step: 0.25 },
];
const VALUE_TOGGLES: Array<{ key: Toggle; label: string; help: string }> = [
  { key: 'mgValuePullOnRecover', label: 'Pull on recovery', help: 'A resting value order comes off once its team’s book has recovered.' },
  { key: 'mgValuePullOnEdge', label: 'Pull on edge loss', help: 'A resting value order comes off when its edge falls under the floor.' },
  { key: 'mgValueExitOnValue', label: 'Sell back on Pinnacle value', help: 'Off: hold what value orders bought to settlement. On: sell back on the other team where it shows edge to unwind, never past flat.' },
];
const CAPACITY: Field[] = [
  { key: 'maxNetContracts', label: 'Max Long (directional exposure)', help: 'Net position plus what her resting orders could add, on either team.', unit: 'ctrs', show: same, store: same, min: 1, max: Infinity, step: 25, whole: true },
  { key: 'gameKillUsd', label: 'Loss cap', help: 'Stops the mini game for good once its P&L on this game — every run of it counted — is down this many dollars. Starting again after a stop needs a higher cap.', unit: '$', show: same, store: same, min: 1, max: 5_000, step: 5, whole: true },
];
const DEPTH: Field[] = [
  { key: 'mgDropPct', label: 'Depleted at or below', help: 'Percent of reference depth remaining.', unit: '%', ...pct, min: 1, max: 99, step: 5, whole: true },
  { key: 'mgRecoverPct', label: 'Recovery at or above', help: 'Measured against the retained reference.', unit: '%', ...pct, min: 1, max: 100, step: 5, whole: true },
  { key: 'mgConfirmMs', label: 'Recovery confirmation', help: 'Continuous time at or above the recovery level.', unit: 's', ...sec, min: 0, max: 60, step: 0.25 },
];
/** NEW-04 (Doom baseline 10-20): the absolute trigger's fields. */
const ABSOLUTE: Field[] = [
  { key: 'mgAbsThreshold', label: 'Best-bid threshold', help: 'Both teams’ best bids holding fewer than this many of other people’s contracts at once starts it.', unit: 'ctrs', show: same, store: same, min: 1, max: Infinity, step: 500, whole: true },
  { key: 'mgConfirmMs', label: 'Recovery confirmation', help: 'Either side at or over the threshold this long pauses new attempts; working ones finish.', unit: 's', ...sec, min: 0, max: 60, step: 0.25 },
];

/** A shown value as typed → the value to store, or why it cannot be. */
export function parseField(f: Field, raw: string): { value: number } | { error: string } {
  const t = raw.trim();
  const v = Number(t);
  if (t === '' || !Number.isFinite(v)) return { error: `${f.label}: a number, please` };
  if (f.whole && !Number.isInteger(v)) return { error: `${f.label}: a whole number` };
  if (v < f.min || v > f.max) return { error: `${f.label}: between ${f.min} and ${f.max} ${f.unit}` };
  return { value: f.store(v) };
}

const num = (cfg: Partial<PenelopeConfig>, k: Key): number => {
  const v = cfg[k];
  return typeof v === 'number' ? v : (MINI_START[k] as number);
};
const fmt = (x: number): string => (Number.isInteger(x) ? x.toLocaleString('en-US') : String(Number(x.toFixed(2))));

export function MiniGamePanel({ game, m, busy, error, bounds }: { game: GameRow; m: PenelopeHarvesterControl; busy: boolean; error: string | null; bounds?: Record<string, PenelopeBound> }) {
  const row = m.game;
  const [draft, setDraft] = useState<Partial<PenelopeConfig>>(MINI_START);
  const [modeChoice, setModeChoice] = useState<'shadow' | 'live'>('shadow');
  const [invalid, setInvalid] = useState<Partial<Record<Key, string>>>({});
  const cfg: Partial<PenelopeConfig> = row?.config ?? draft;
  const armed = row?.config.enabled === true;
  const mode: 'shadow' | 'live' = row ? (row.config.mode === 'live' ? 'live' : 'shadow') : modeChoice;
  const live = row?.live?.baseline ?? null;
  const matchup = `${game.awayCode} @ ${game.homeCode}`;
  // No row of its own, but one of hers on this game stopped itself within the day: say so, with the reason — until
  // 2026-10-21 a tripped loss cap read as "not started" and the switch simply sat Off.
  const stopped = row ? null : m.lastStopped ?? null;
  const sentence = row
    ? penelopeStatusSentence({ armed, parked: row.parked, practice: mode !== 'live', teamA: game.awayCode, teamB: game.homeCode, live })
    : stopped ? 'stopped on its own — run again below' : 'not started — settings ready for the first run';

  // A field's range on this box: the relay's bound where it sends one (Max Long 50,000 and Contract Size 10,000 on node1).
  const ranged = (f: Field): Field => {
    const b = bounds?.[f.key];
    return b ? { ...f, min: f.show(b.min), max: f.show(b.max) } : f;
  };
  const save = (f0: Field, raw: string): void => {
    const f = ranged(f0);
    const r = parseField(f, raw);
    if ('error' in r) { setInvalid((x) => ({ ...x, [f.key]: r.error })); return; }
    setInvalid((x) => { const next = { ...x }; delete next[f.key]; return next; });
    if (r.value === num(cfg, f.key)) return;
    // Max Long (directional exposure) is the row's Max Long on both teams: the lean cap follows it.
    const patch: Partial<PenelopeConfig> = f.key === 'maxNetContracts' ? { maxNetContracts: r.value, leanMaxNetContracts: r.value } : { [f.key]: r.value };
    if (row) void m.patch(patch);
    else setDraft((d) => ({ ...d, ...patch }));
  };

  const toggle = (on: boolean): void => {
    if (!on) { if (row) void m.setArm(false); return; }
    if (mode === 'live' && !window.confirm(`Turn the mini game on LIVE on ${matchup}? Real orders, ${fmt(num(cfg, 'clipContracts'))} a side.`)) return;
    if (row) void m.setArm(true);
    else void m.run({ ...draft, mode });
  };
  const trigger: 'percent' | 'absolute' = cfg.mgTriggerMethod === 'absolute' ? 'absolute' : 'percent';
  const mgMode: 'pair' | 'value' = cfg.mgMode === 'value' ? 'value' : 'pair';
  const pickMgMode = (next: 'pair' | 'value'): void => {
    if (next === mgMode) return;
    if (row) void m.patch({ mgMode: next });
    else setDraft((d) => ({ ...d, mgMode: next }));
  };
  const flag = (k: Toggle): boolean => (typeof cfg[k] === 'boolean' ? (cfg[k] as boolean) : (MINI_START[k] as boolean));
  const setFlag = (k: Toggle, on: boolean): void => {
    if (row) void m.patch({ [k]: on });
    else setDraft((d) => ({ ...d, [k]: on }));
  };
  const lockC = typeof cfg.mgCompletionLockCents === 'number' ? cfg.mgCompletionLockCents : null;
  const pickTrigger = (next: 'percent' | 'absolute'): void => {
    if (next === trigger) return;
    if (row) void m.patch({ mgTriggerMethod: next });
    else setDraft((d) => ({ ...d, mgTriggerMethod: next }));
  };
  const pickMode = (next: 'shadow' | 'live'): void => {
    if (row) { if (!armed && next !== mode) void m.patch({ mode: next }); }
    else setModeChoice(next);
  };

  const fieldRow = (f0: Field) => {
    const f = ranged(f0);
    const id = `minigame-${f.key}`;
    const err = invalid[f.key];
    const shown = f.show(num(cfg, f.key));
    return (
      <div key={f.key} className="grid grid-cols-[minmax(0,1fr)_7.25rem] items-start gap-3 py-1.5">
        <label htmlFor={id} className="block pt-1 text-[12px] leading-5 text-[#e1e8f3]">
          {f.label}
          <span className="mt-0.5 block text-[11px] leading-snug text-[#9cacc1]">{f.help}</span>
        </label>
        <div className="grid grid-cols-[minmax(0,1fr)_2rem] items-center gap-1.5 pt-px">
          <input
            id={id} type="number" inputMode="decimal" step={f.step} min={f.min} max={Number.isFinite(f.max) ? f.max : undefined}
            key={`${row?.id ?? 'draft'}-${f.key}-${shown}`} defaultValue={String(shown)} disabled={busy}
            aria-invalid={err ? 'true' : undefined} aria-label={`mini game ${f.label}`}
            className={cn('h-7 w-full min-w-0 rounded-[5px] border bg-[#111b27] px-1.5 text-center text-[12px] tabular-nums text-[#e9f0fa] hover:border-[#445b76] disabled:opacity-60', err ? 'border-[#eb8f88]' : 'border-[#2c3e53]')}
            onBlur={(e) => save(f, e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          />
          <span className="whitespace-nowrap text-[11px] text-[#a4b2c7]">{f.unit}</span>
        </div>
        {err && <p role="alert" className="col-span-2 -mt-1 text-[11px] text-[#ffafa6]">{err} — not saved</p>}
      </div>
    );
  };
  const readOnly = (label: string, value: string) => (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-[11px] text-[#96a9bf]">
      <span>{label}</span>
      <span className="text-right text-[#c6d3e3]">{value}</span>
    </div>
  );
  const section = (title: string, summary: string, open: boolean, body: ReactNode) => (
    <details open={open} className="border-t border-[#29384b]" data-testid={`minigame-section-${title}`}>
      <summary className="cursor-pointer select-none py-2.5 text-[11px] uppercase leading-[18px] tracking-[.045em] text-[#aab8cb] marker:text-[#899ab0]">
        {title}
        <span className="float-right ml-2 normal-case tracking-normal tabular-nums text-[#8e9fb5]">{summary}</span>
      </summary>
      <div className="pb-2.5">{body}</div>
    </details>
  );

  const note = (text: ReactNode) => <p className="my-1 text-[11px] leading-snug text-[#9cacc1]">{text}</p>;
  const windowS = fmt((typeof cfg.mgWindowMs === 'number' ? cfg.mgWindowMs : 10_000) / 1000);

  return (
    <section aria-label="Penelope mini game" data-testid="minigame-panel"
      className="w-full rounded-[5px] border border-[#29394b] bg-[#172231] px-3 pb-3 font-sans text-[12px] leading-[1.42] text-[#e8edf5] [color-scheme:dark]">
      <header className="flex items-center justify-between gap-3 border-b border-[#2a384a] py-3">
        <h3 className="m-0 text-[14px] font-semibold text-[#edf2f8]">Penelope <span className="font-normal text-[#9babbe]">· mini game</span></h3>
        <span className="rounded border border-[#35475d] px-1.5 py-px text-[10px] tracking-[.03em] text-[#9aacc1]">V1</span>
      </header>

      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 pb-2 pt-3">
        <label htmlFor="minigame-enable" className="block text-[12px] text-[#f0f3f8]">
          Enable mini game
          <span className="mt-0.5 block text-[11px] text-[#9cacc1]">{mgMode === 'value' ? 'Place value orders on a team in its own depth dropout' : 'Place pairs during a two-sided depth dropout'} on {matchup}.</span>
        </label>
        <input
          id="minigame-enable" type="checkbox" role="switch" aria-label="Enable mini game" checked={armed} disabled={busy}
          onChange={(e) => toggle(e.target.checked)}
          className={cn(
            'relative block h-[21px] w-[37px] cursor-pointer appearance-none rounded-full border transition-colors disabled:opacity-50',
            "before:absolute before:left-[2px] before:top-[2px] before:h-[15px] before:w-[15px] before:rounded-full before:transition-transform before:content-['']",
            !armed ? 'border-[#455369] bg-[#303e50] before:bg-[#c1cbd8]'
              : mode === 'live' ? 'border-red-400 bg-red-600 before:translate-x-4 before:bg-[#f6f9ff]'
                : 'border-[#2683e9] bg-[#1972d5] before:translate-x-4 before:bg-[#f6f9ff]',
          )}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 pb-2 text-[11px]">
        <span className="text-[#9caec2]">orders</span>
        <div role="radiogroup" aria-label="mini game practice or live" className="flex overflow-hidden rounded border border-[#35475d]">
          {(['shadow', 'live'] as const).map((v) => (
            <button key={v} type="button" role="radio" aria-checked={mode === v}
              disabled={busy || armed || (v === 'live' && !m.liveEligible)}
              title={armed ? 'Turn it off to switch' : v === 'live' && !m.liveEligible ? 'Live orders are not available to you here' : undefined}
              onClick={() => pickMode(v)}
              className={cn('px-2 py-0.5 font-bold uppercase disabled:opacity-50', mode === v ? (v === 'live' ? 'bg-red-600 text-white' : 'bg-[#1972d5] text-white') : 'text-[#9aacc1]')}>
              {v === 'live' ? 'live' : 'practice'}
            </button>
          ))}
        </div>
        {mode === 'live' && <span className="font-bold text-red-400">real orders on Kalshi</span>}
      </div>

      {stopped && (
        <p role="alert" data-testid="minigame-stopped" className="mb-2 rounded border border-red-400/60 bg-red-500/10 px-2 py-1 text-[11px] text-red-300">
          Stopped {new Date(stopped.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {stopped.mode === 'shadow' ? ' (practice)' : ''}: {stopped.stopReason ?? 'no reason recorded'}. Run again below.
        </p>
      )}
      <p className="flex min-h-[28px] items-baseline gap-2 pb-2 text-[11px] text-[#9caec2]" data-testid="minigame-status" data-enabled={armed}>
        <span className={cn('inline-block h-1.5 w-1.5 shrink-0 self-center rounded-full', armed ? 'bg-[#35bf91]' : 'bg-[#75859a]')} />
        <span className={cn('font-semibold', armed ? 'text-[#58d3a9]' : 'text-[#c1ccda]')}>{armed ? 'On' : 'Off'}</span>
        <span className="text-[#91a3b9]">{sentence}</span>
      </p>
      {live && (
        <p className="pb-2 font-mono text-[10px] text-[#9cacc1]">
          held {live.inv.A.contracts} {game.awayCode} / {live.inv.B.contracts} {game.homeCode} · net {live.net} · P&amp;L{mode === 'live' ? '' : ' (practice)'} ${(live.pnlCents / 100).toFixed(2)}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 pb-2 text-[11px]">
        <span className="text-[#9caec2]">mode</span>
        <div role="radiogroup" aria-label="mini game mode" className="flex overflow-hidden rounded border border-[#35475d]">
          {(['pair', 'value'] as const).map((v) => (
            <button key={v} type="button" role="radio" aria-checked={mgMode === v} disabled={busy} onClick={() => pickMgMode(v)}
              className={cn('px-2 py-0.5 disabled:opacity-50', mgMode === v ? 'bg-[#1972d5] text-white' : 'text-[#9aacc1]')}>
              {v === 'pair' ? 'pair attempts' : 'value side only'}
            </button>
          ))}
        </div>
      </div>
      {mgMode === 'value' ? section('Value orders', `${fmt(num(cfg, 'mgValueClipContracts'))} / order · ${fmt(num(cfg, 'mgValueDropoutCapContracts'))} per dropout`, true, (
        <>
          {VALUE.map(fieldRow)}
          {VALUE_TOGGLES.map((t) => (
            <div key={t.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 py-1.5">
              <label htmlFor={`minigame-${t.key}`} className="block text-[12px] leading-5 text-[#e1e8f3]">
                {t.label}
                <span className="mt-0.5 block text-[11px] leading-snug text-[#9cacc1]">{t.help}</span>
              </label>
              <input id={`minigame-${t.key}`} type="checkbox" aria-label={`mini game ${t.label}`} checked={flag(t.key)} disabled={busy} onChange={(e) => setFlag(t.key, e.target.checked)} className="mt-1.5" />
            </div>
          ))}
          {note('No pairing: one value order at a time across both teams, joining the best bid of the team in its own dropout. Nothing new while Pinnacle is off; a resting order runs out its lifetime.')}
        </>
      )) : section('Pair attempts', `${fmt(num(cfg, 'clipContracts'))} / side · ${fmt(num(cfg, 'pairLockCents'))}¢ min`, true, (
        <>
          {PAIR.map(fieldRow)}
          {note('Working pairs keep their prices and original deadlines. V1 runs one attempt at a time; an orphan completion keeps working beside a new attempt.')}
        </>
      ))}
      {section('Capacity', `${fmt(num(cfg, 'maxNetContracts'))} contracts · $${fmt(num(cfg, 'gameKillUsd'))} loss cap`, true, (
        <>
          {CAPACITY.map(fieldRow)}
          {readOnly('Max orphan slots', 'not in V1 — directional exposure bounds it')}
        </>
      ))}
      {section('Depth dropout', trigger === 'absolute'
        ? `under ${fmt(num(cfg, 'mgAbsThreshold'))} · ${fmt(sec.show(num(cfg, 'mgConfirmMs')))} s`
        : `${fmt(pct.show(num(cfg, 'mgDropPct')))}% / ${fmt(pct.show(num(cfg, 'mgRecoverPct')))}% · ${fmt(sec.show(num(cfg, 'mgConfirmMs')))} s`, true, (
        <>
          <div className="flex flex-wrap items-center gap-2 py-1.5 text-[11px]">
            <span className="text-[#9caec2]">trigger</span>
            <div role="radiogroup" aria-label="mini game trigger method" className="flex overflow-hidden rounded border border-[#35475d]">
              {(['percent', 'absolute'] as const).map((v) => (
                <button key={v} type="button" role="radio" aria-checked={trigger === v} disabled={busy} onClick={() => pickTrigger(v)}
                  className={cn('px-2 py-0.5 disabled:opacity-50', trigger === v ? 'bg-[#1972d5] text-white' : 'text-[#9aacc1]')}>
                  {v === 'percent' ? '% of reference' : 'absolute size'}
                </button>
              ))}
            </div>
          </div>
          {trigger === 'absolute' ? (
            <>
              {ABSOLUTE.map(fieldRow)}
              {note('Both best bids under the threshold together to start. Either side at or over it for the confirmation time pauses new attempts.')}
              <div className="mt-1 border-t border-[#253548] pt-2">
                {readOnly('Measured', 'Other people’s contracts at each best bid')}
                {note('Every Penelope order on this box is excluded — hers, Penelope’s and Puppy Love’s.')}
              </div>
            </>
          ) : (
            <>
              {DEPTH.map(fieldRow)}
              {note('Both sides depleted to start. Either side recovered pauses new attempts.')}
              <div className="mt-1 border-t border-[#253548] pt-2">
                {readOnly('Depth measured', 'External best bid + next 2 ticks')}
                {readOnly('Reference', `${windowS}-second time-sampled median (every 0.25 s)`)}
                {note('Reference depth stays fixed during depletion. Every Penelope order on this box is excluded; empty ticks count as zero.')}
              </div>
            </>
          )}
        </>
      ))}
      {mgMode === 'pair' && section('Orphan completions', lockC === null ? 'Penelope value rules' : `must lock ${fmt(lockC)}¢`, false, (
        <>
          {readOnly('Value buying', 'This row’s edge to add, against Pinnacle')}
          {readOnly('Execution', 'Maker only, at the best bid')}
          {readOnly('Completion size', 'Up to the unpaired contracts')}
          {note('The completion is pulled when Pinnacle becomes unusable or its edge reaches zero or below. If no qualifying exit occurs, the orphan is held through settlement.')}
          {lockC === null
            ? note('Judged on Pinnacle value, not on what the orphan cost: a completion can make a pair that cost more than 100¢.')
            : note(`Must also lock (desk setting): the orphan’s average price + the completion price + its fee at most ${fmt(100 - lockC)}¢; a resting one that no longer does comes off.`)}
          {note('V1: orphans are not kept as separate records — the row’s unpaired contracts are completed together. A completion keeps working beside a new attempt; none is placed beside a working attempt leg on its own side.')}
        </>
      ))}

      <footer className="flex flex-wrap items-center gap-2 border-t border-[#29384b] pt-2.5 text-[10px] leading-snug text-[#8195af]">
        <span className="min-w-0 flex-1">Its own game row: its own orders, fills and P&amp;L. Penelope’s caps do not count its position.</span>
        {row && (
          <button type="button" disabled={busy} className="uppercase text-[#9aacc1] hover:text-[#e8edf5] disabled:opacity-50"
            onClick={() => { if (window.confirm('Remove the mini game from this game? Its orders are cancelled; its position stays yours.')) void m.remove(); }}>
            remove
          </button>
        )}
      </footer>
      {error && <p role="alert" className="mt-2 text-[11px] text-[#ffafa6]">{error}</p>}
    </section>
  );
}
